import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface BuildRow {
  readonly buildId: string;
  readonly buildCode: string;
  readonly application: string;
  readonly buildVersion: string | null;
  readonly status: string;
  readonly pipelineId: string | null;
  readonly durationSeconds: number | null;
}

const STATUSES = ['', 'QUEUED', 'RUNNING', 'SUCCESS', 'FAILED', 'CANCELLED'];

/**
 * M32 — Build Monitoring Dashboard (§31 / §16). Live view of build executions
 * (T424) with application, version, duration and outcome. Filterable by status
 * and application; feeds the BR-06 build-success-rate governance gate that must
 * stay above the Global-Parameter-configured threshold.
 */
export function BuildMonitoringPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<BuildRow[]>([]);
  const [status, setStatus] = useState('');
  const [application, setApplication] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (application) params.set('application', application);
    const qs = params.toString();
    apiRequest<{ data: BuildRow[] }>(`/v1/devops/builds${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, application]);

  return (
    <main aria-label="build-monitoring">
      <h1>{t('m32.build.title')}</h1>
      <div className="filters">
        <label>
          {t('m32.build.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m32.build.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m32.build.application')}
          <input aria-label="filter-application" value={application} onChange={(e) => setApplication(e.target.value)} placeholder={t('m32.build.allPlaceholder')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m32.build.loading')}</p> : (
        <>
          <p aria-label="build-count">{t('m32.build.count', { count: rows.length })}</p>
          <table aria-label="build-table">
            <thead><tr><th>{t('m32.build.thCode')}</th><th>{t('m32.build.application')}</th><th>{t('m32.build.thVersion')}</th><th>{t('m32.build.thDuration')}</th><th>{t('m32.build.status')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.buildId} data-status={r.status}>
                  <td>{r.buildCode}</td><td>{r.application}</td><td>{r.buildVersion ?? '—'}</td>
                  <td>{r.durationSeconds ?? '—'}</td><td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
